/**
 * Conversations — shared type contract.
 * Design: docs/plans/conversations/README.md + 01-decisions.md + 02-data-model.md
 *
 * Lot 1 types — all v1 fields.
 * Deferred fields are ABSENT; a DTO carrying one is a 400 naming it (§0.4, §6.1).
 * Everything billing-related here is the COST constant only; the gauge/meter
 * registry entries and the billing-doc updates land in Lot 3 (§5.5).
 */

import type { Cadence } from './cadence';
import type { GeoSuggestion, TargetCategory, TargetZone } from './targets';
import type { DomainClassification } from './audit';
import type { BrandQueryIntent } from './brand-query';

// Re-export so importers can get TARGET_CATEGORIES from either module.
export { TARGET_CATEGORIES } from './targets';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** D6 — platform ceiling on turns per conversation. Token cost is quadratic in turns. */
export const CONVERSATION_MAX_TURNS = 6;

/**
 * Max turns per run (owner, 2026-10-30): 6 on every brand tier; an agency sets 1..6 per
 * conversation. The ONE resolver of the save, `runNow` and the sweep, so the hold, the
 * RUN# config and the commit always use the same number.
 */
export function resolveConversationMaxTurns(tier: string, requested?: number | null): number {
  if (tier !== 'agency') return CONVERSATION_MAX_TURNS;
  const n = Math.floor(requested ?? CONVERSATION_MAX_TURNS);
  return Math.min(Math.max(Number.isFinite(n) ? n : CONVERSATION_MAX_TURNS, 1), CONVERSATION_MAX_TURNS);
}

/**
 * The three size ceilings (lot I2, P4): STRUCTURAL, checked at save (400), never at launch,
 * never shown as a quota, never resolved per tier. The money bound is the per-brand
 * `brand_conversation_runs` allowance (C17/C18), not these.
 *
 * Max distinct (engine × language × zone) cells per conversation (was 12 until lot I2).
 * Above `ROLLUP_INLINE_MAX_CELLS` the config and the rollup live in their own items
 * (`BPCELL#` / `BPOPT#` / `RCELL#`, utils/conversationConfigStore.ts).
 */
export const CONVERSATION_MAX_CELLS = 300;

/** Max Σ cells[].runs per launch (was 20 until lot I2). Each run ≤ CONVERSATION_MAX_TURNS turns. */
export const CONVERSATION_MAX_RUNS = 5000;

/**
 * Max `cells[i].runs` (today's former CONVERSATION_MAX_RUNS value, so every stored cell stays
 * valid). Bounds one cell's `RUN#` summary (its per-run `runs[]` array) and its `RUNX#` row.
 */
export const CONVERSATION_MAX_RUNS_PER_CELL = 20;

/**
 * Structural per-brand cap on non-archived personas — NOT a quota gauge (§2.3, §5.4).
 * What it protects: the brand job's `begins_with(SK,'PLEDGER#')` Query (O(personas))
 * and the one-page `PSUM#` personas table. Enforced at POST /personas and duplicate
 * with its own distinct UI message separate from the tenant-wide `conversation_personas`
 * message (400 even while the tenant gauge still has slots).
 */
export const CONVERSATION_MAX_PERSONAS_PER_BRAND = 50;

/**
 * D10 — number of shards in the due-index GSI. BAKED INTO every stored `dueBucket`
 * string: raising it later strands every existing persona in a shard no sweep reads.
 * Chosen ONCE at Lot 1, NEVER changed (§2.2).
 */
export const CONVERSATION_DUE_SHARDS = 256;

/**
 * D46 — 13-digit reverse-timestamp epoch used by `reverseTs`.
 * `padStart(13,'0')` is not cosmetic: a digit-boundary crossing sorts wrong without it.
 * The repo has four incompatible bases; new writers import this and nothing else.
 */
export const REVERSE_TS_EPOCH = 9_999_999_999_999;

/**
 * §3.4 step 0b — longest message the persona may send via `ask_engine`.
 * A guardrail against injected-reply paste-back, not a hygiene cap: input tokens are
 * 56 % of unit cost and the history is re-sent every turn.
 * Keep separate from CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS (different rejection paths).
 */
export const CONVERSATION_MAX_MESSAGE_CHARS = 2000;

/**
 * D40 — longest pinned (scripted) question text validated at save.
 * Far below MAX_MESSAGE_CHARS: a pinned question is a buyer question, not an LLM payload.
 */
export const CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS = 400;
/** I1 step 2/5 — max scripted options per turn (mixed mode). */
export const CONVERSATION_MAX_TURN_OPTIONS = 5;

/**
 * I1 step 2 — source of a scripted turn item (lot I step 4/5).
 * `text` = free text; `prompt` = BrandQuery from « Mes prompts » (refId = queryId);
 * `objection` = from BrandPersona.objections / BrandStrategy.customerObjections;
 * `question` = from BrandStrategy.customerQuestions (the API's accepted value; this type said
 * `customer_question` until lot I3, which the save 400'd).
 */
export type ConversationScriptSource = 'text' | 'prompt' | 'objection' | 'question';

/** One scripted item. Several entries with one `turn` = that turn's options (`mode: 'mixed'` only). */
export interface ConversationScriptedQuestion {
  turn: number;
  /** ≤ CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS, sent byte-for-byte. */
  text: string;
  /** Absent on old rows = `'text'`. */
  source?: ConversationScriptSource;
  /** `source: 'prompt'` only: the `BrandQuery.queryId` (Couverture terms). */
  refId?: string;
}

/** D41 — Cibles matches kept per turn (sent as `maxMatches` to targets-search). */
export const CONVERSATION_MAX_TURN_MATCHES = 40;

/**
 * GAP-2 — ids kept in `ConversationSubRunExtras.citedTargets`, across ALL categories.
 * Sized so the addition stays small against RUNX#'s 20–50 KB: 1000 × ~28 B ≈ 28 KB.
 * A sub-run (≤ 20 runs × ≤ 6 turns, ≤ 40 matches/turn) is far under this in practice;
 * the cap exists so a catalogue-scale brand cannot grow the row without bound.
 * Tripping it sets `citedTargetsTruncated` and makes that day's distinct count a FLOOR.
 */
export const CONVERSATION_MAX_CITED_TARGET_IDS = 1000;

/** Retention (2026-10-05): every aggregate row is kept 2 years — the timeframe selector goes to 2 years. */
export const CONVERSATION_TDAY_TTL_DAYS = 730;

/**
 * Days of `PXDAY#` history kept. Headroom over the 90-day max window so a read at the
 * far edge of the timeframe selector never lands on a row the TTL has already reaped.
 */
export const CONVERSATION_PXDAY_TTL_DAYS = 730;

/**
 * GAP-2 — days the fat per-day working set (`TSET#`) is kept. It is never read by the API,
 * only accumulated and read back in the same UpdateItem, so it needs to outlive nothing but
 * a late-closing launch.
 */
export const CONVERSATION_TSET_TTL_DAYS = 3;

/**
 * §7.8 — max user-defined tags per persona. Lowercase-kebab, ≤ 30 chars each.
 * Derived tags (`intent:<id>`, `mode:<mode>`) are computed at display time and
 * never counted toward this cap.
 */
export const CONVERSATION_MAX_TAGS = 20;

/**
 * §4.2 — caps on the persona's free-text fields, enforced at save (create + PATCH).
 * The dispatcher concatenates these verbatim into the system prompt on EVERY turn, so an
 * uncapped field is prompt-cost amplification on platform money. The 600 / 5 / 5 values
 * restate the `ConversationPersona` field comments; the others are new.
 */
export const CONVERSATION_MAX_PERSONA_NAME_CHARS = 80;
export const CONVERSATION_MAX_PERSONA_DESCRIPTION_CHARS = 600;
export const CONVERSATION_MAX_PERSONA_AGE_RANGE_CHARS = 40;
export const CONVERSATION_MAX_PERSONA_BUDGET_CHARS = 80;
/** `objections`, `decisionCriteria`, `comparisonSet` — items per list, chars per item. */
export const CONVERSATION_MAX_PERSONA_LIST_ITEMS = 5;
export const CONVERSATION_MAX_PERSONA_LIST_ITEM_CHARS = 200;

/**
 * D11 — credits billed per COMPLETED turn. Failed turns bill nothing (D7).
 * `maxTurns` is therefore both a cost control and a billing input; a run that ends
 * early via `end_conversation` bills fewer credits.
 */
export const CREDIT_COST_CONVERSATION_TURN = 2;

/**
 * D37 — minimum measured personas per intent window for the intent to contribute
 * to the brand's global score. PLATFORM constant, never a tenant setting:
 * a tunable gate makes two accounts incomparable.
 */
export const CONVERSATION_MIN_COVER = 3;

export const CONVERSATION_WINDOWS = ['7d', '30d', '90d'] as const;

/** D52 — ledger retained 2 years (was 180 d until 2026-10-05); every prev window is real. */
export const CONVERSATION_LEDGER_DAYS = 730;

/**
 * Retention (2026-10-05, supersedes the D15 thinning cascade):
 *  - conversation DETAIL (`RUN#<launchId>#<ck>` partition: `CONV#` + `TURN#` rows) carries a
 *    60-day `ttl` stamped at write time — nobody reads a transcript that old;
 *  - every per-cell SUMMARY (`RUN#`/`RUNX#` under `PERSONA#`) carries a 2-year `ttl`, so the
 *    persona view can page back two years with sources/URLs/cibles intact.
 * No prune cascade: DynamoDB TTL is the only collector. Python mirrors these two numbers
 * (it cannot import TS) — keep them in lock-step.
 */
export const CONVERSATION_TRANSCRIPT_TTL_DAYS = 60;
export const CONVERSATION_SUMMARY_TTL_DAYS = 730;

/**
 * D56 — per-tenant worst-case turns RESERVED at dispatch today, so the daily cap is
 * enforced atomically across concurrent sweep shards and manual launches (the committed
 * meter `conversation_turns_daily#<day>#<HH>` only moves at commit, minutes later).
 * One row per tenant per UTC day: PK `METER#<tenantId>`, SK from this helper, attribute `n`.
 * Gate = `ADD n :worst` with `ConditionExpression: attribute_not_exists(n) OR n <= :headroom`
 * where `:headroom = dailyLimit - worst`. Same table and TTL as the committed meter.
 */
export const conversationDispatchedMeterSk = (dayIso: string): string =>
  `conversation_turns_dispatched#${dayIso}`;

/**
 * D37 — browser-only intent mix presets for the brand score.
 * The score is in NO API response (computed client-side from window slices already delivered).
 */
export const CONVERSATION_MIX_PRESETS = {
  trafic: { label: 'Trafic estimé', mix: { learn: 60, discover: 30, compare: 10 } },
  egal:   { label: 'Parts égales',  mix: { learn: 34, discover: 33, compare: 33 } },
} as const;

// ---------------------------------------------------------------------------
// Engine list (D2)
// ---------------------------------------------------------------------------

/**
 * D2 — the five conversation engines. NOT `ai_overview` (no conversational surface)
 * and NOT `claude` (not a conversation engine). Both are rejected in the DTO.
 *
 * `gemini` and `google` are two different Google surfaces, both measured (D61bis,
 * 2026-09-28): `gemini` is the legacy `generateContent` + `googleSearch` transport,
 * `google` is the Interactions API with live `google_search` + `google_maps`
 * grounding. `google` is NOT "AI Mode" — no such transport is available from Google
 * or from any SERP provider we use. Adding `google` costs one extra sub-run per
 * launch (D2: one sub-run per cell).
 */
export type ConversationEngine = 'chatgpt' | 'perplexity' | 'gemini' | 'google' | 'mistral';
export const CONVERSATION_ENGINES: readonly ConversationEngine[] = [
  'chatgpt',
  'perplexity',
  'gemini',
  'google',
  'mistral',
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * D46 — reverse timestamp string: `(REVERSE_TS_EPOCH − ms).toString().padStart(13,'0')`.
 * Lexicographic order is newest-first by SCHEDULED time (the claimed tick, not the
 * actual start). Never re-derive from `startedAt`.
 */
export function reverseTs(ts: number | Date): string {
  const ms = ts instanceof Date ? ts.getTime() : ts;
  return String(REVERSE_TS_EPOCH - ms).padStart(13, '0');
}

/** djb2-like hash, 32-bit unsigned, deterministic. */
function _djb2(s: string, seed = 5381): number {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h, 33) ^ s.charCodeAt(i)) >>> 0;
  }
  return h;
}

const _B32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Encode the lower 20 bits of a 32-bit unsigned integer as 4 Crockford base32 chars, MSB first.
 * 4 chars × 5 bits = 20 bits per hash; two hashes → 8 chars (40 bits) in newLaunchId.
 */
function _enc4(n: number): string {
  const v = n >>> 0;
  return (
    _B32[(v >>> 15) & 31] +
    _B32[(v >>> 10) & 31] +
    _B32[(v >>> 5)  & 31] +
    _B32[v          & 31]
  );
}

/**
 * D51/D54 — launch id = `reverseTs(scheduledFor)` + `'-'` + 8-char base32 hash of personaId.
 * Deterministic: a retried sweep mints the same id, and every row write is an idempotent
 * overwrite. Two personas at the same `scheduledFor` produce distinct ids via the hash suffix.
 */
export function newLaunchId(personaId: string, scheduledFor: number | Date): string {
  const h1 = _djb2(personaId, 5381);
  const h2 = _djb2(personaId, 0x9e3779b9);
  return `${reverseTs(scheduledFor)}-${_enc4(h1)}${_enc4(h2)}`;
}

/** D46 — recover the scheduled epoch milliseconds from a launchId. */
export function launchTs(launchId: string): number {
  return REVERSE_TS_EPOCH - Number(launchId.slice(0, 13));
}

/**
 * I1 step 2 — the zone segment used for Mondial (no zone) cells in the DDB key and
 * in display. A stored cell with no `zone` uses this segment; `parseCellPath` maps
 * it back to `zone: undefined`.
 */
export const CONVERSATION_WORLD_CELL_ZONE = 'world' as const;

/**
 * The ONLY way to build a cell's DynamoDB key (coarsest → finest: engine, language, zone).
 * Used in the SK, the transcript PK, and the counter PK. Never inline.
 * `zone` absent = Mondial → uses CONVERSATION_WORLD_CELL_ZONE.
 */
export function cellKey(engine: ConversationEngine, language: string, zone?: string): string {
  return `${engine}#${language}#${zone ?? CONVERSATION_WORLD_CELL_ZONE}`;
}

/**
 * URL-safe form of the cell key (`#` starts a URL fragment; `~` does not).
 * Round-trips with `parseCellPath`.
 */
export function cellPath(engine: ConversationEngine, language: string, zone?: string): string {
  return `${engine}~${language}~${zone ?? CONVERSATION_WORLD_CELL_ZONE}`;
}

/** Inverse of `cellPath`. Returns `null` on malformed input or unknown engine. */
export function parseCellPath(
  p: string,
): { engine: ConversationEngine; language: string; zone?: string } | null {
  const parts = p.split('~');
  if (parts.length !== 3) return null;
  const [e, language, zone] = parts;
  if (!(CONVERSATION_ENGINES as readonly string[]).includes(e)) return null;
  return {
    engine: e as ConversationEngine,
    language,
    zone: zone === CONVERSATION_WORLD_CELL_ZONE ? undefined : zone,
  };
}

/**
 * D32 — derive the zone key from a GeoSuggestion.
 * Format: `FR` for a country; `BE-anvers` (countryCode + `-` + 1-24-char slug) for a place.
 * The slug is a pure function of the suggestion so the same city always yields the same zone key.
 */
export function zoneKeyFrom(s: GeoSuggestion): string {
  if (s.type === 'country') return s.countryCode;
  const raw = s.cityName ?? s.regionName ?? s.label;
  const slug = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacriticals (é → e)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
  return `${s.countryCode}-${slug}`;
}

// ---------------------------------------------------------------------------
// Union types / enums
// ---------------------------------------------------------------------------

export type ConversationMode   = 'improvised' | 'mixed' | 'scripted';
export type ConversationStatus = 'active' | 'paused' | 'archived';

/**
 * D36 — one ordinal outcome code per run.
 * Numeric 1..maxTurns = dropped at that turn (last cited turn was t-1).
 * 'M' = cited on the final completed turn, not recommended.
 * 'R' = recommended.
 * 'x' = engine failure — OUTSIDE every denominator.
 */
export type ConversationOutcome = number | 'M' | 'R' | 'x';

/** D13 — analysis layer provenance. */
export type ConversationSignalProvenance = 'regex' | 'llm';

/**
 * D29 — reason a conversation ended.
 * `'stopped_early'` is the honest default: the agent stopped calling `ask_engine`
 * without any terminal condition firing (added 2026-09-25 — the runtime initialised
 * `endedBy` to `'max_turns'`, so every early stop reported « 6 tours sans décider »
 * on runs that had completed 0 or 1 turns).
 */
export type ConversationEndedBy =
  | 'agent_says_done'
  | 'max_turns'
  | 'script_done'
  | 'stopped_early';

/**
 * Status of the LLM analysis step (Layer B).
 * On a sub-run: `ready` means all turns have landed. On a turn: `ready` or `failed`.
 */
export type ConversationAnalysisStatus = 'pending' | 'ready' | 'partial' | 'failed';

/** D44 — normalized turn stage, a pure function of turn number. */
export type ConversationStage = 'exploration' | 'evaluation' | 'decision';

/**
 * Full status of a sub-run. `blocked_quota` and `blocked_gate` are terminal without credits
 * spent (D57 — a claim that could not launch still advances `nextRunAt`).
 */
export type ConversationRunStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'partial'
  | 'blocked_quota'
  | 'blocked_gate'
  | 'failed';

export type ConversationTone =
  | 'neutral'
  | 'direct'
  | 'skeptical'
  | 'enthusiastic'
  | 'technical'
  | 'novice';

/** D61 — How the persona scopes brand comparisons in its questions. */
export type ConversationSelectionFocus = 'broad' | 'focused';

/** D61 — Self-assessed knowledge of the product/service domain. */
export type ConversationDomainExpertise = 'novice' | 'intermediate' | 'expert';

/** D61 — Writing register for the messages sent to the engine. */
export type ConversationLanguageStyle = 'professional' | 'casual' | 'typos';

/** D61 — How readily the persona accepts or challenges AI answers. */
export type ConversationReceptiveness = 'accepting' | 'curious' | 'challenger';

export type ConversationWindow = (typeof CONVERSATION_WINDOWS)[number];

// ---------------------------------------------------------------------------
// Intent registry (D35/D23)
// ---------------------------------------------------------------------------

export type ConversationIntentId = 'learn' | 'discover' | 'compare' | 'review';

/**
 * I1 step 2 — display + iteration order. Every UI list of persona intents uses
 * this constant, never a literal array (so adding 'review' here is sufficient).
 */
export const CONVERSATION_INTENT_ORDER: readonly ConversationIntentId[] = [
  'learn', 'discover', 'compare', 'review',
] as const;

/**
 * D23 — frozen registry row shape. Custom intents (v2) are "allow writes to a table
 * that already has three rows" — every call site goes through `resolveIntent(id)`.
 * The headlineMetric's `signal` must have an ADDITIVE counter in `stats`; see the
 * note on `sumSourceTurns` in `ConversationSubRun.stats`.
 */
export interface ConversationIntent {
  id: ConversationIntentId;
  /** UI label. */
  name: string;
  /** Goes into the persona's system prompt. */
  description: string;
  /** Example question shown in the editor. */
  exampleQuestion: string;
  /** ≤ 200 chars prose addressed to the persona (rendered next to `end_conversation`). */
  exitCondition: string;
  lostWhen: 'not_source' | 'not_cited' | 'eliminated';
  /**
   * The metric each intent is judged on. `learn` → `brandIsSource` rate;
   * `discover` / `compare` → exit turn of the corresponding signal.
   * `kind: 'rate'` means the colour ramp's high end is good; `'exit_turn'` means lower is better.
   */
  headlineMetric: {
    signal: 'brandIsSource' | 'dropOutTurn' | 'eliminatedAtTurn';
    kind: 'rate' | 'exit_turn';
  };
  /**
   * Gagné / partiel / perdu prose and the outcome verb. Render-time only;
   * gagné/perdu are derived from the outcome code per intent — never stored on a row.
   */
  outcomes: { win: string; part: string; lose: string; verb: string };
  /** learn: 4 · discover: 6 · compare: 6 */
  defaultMaxTurns: number;
}

export const CONVERSATION_INTENTS: Record<ConversationIntentId, ConversationIntent> = {
  learn: {
    id: 'learn',
    name: 'Apprentissage',
    description:
      "Tu es un acheteur ou prescripteur qui cherche à comprendre ce qui existe dans un domaine. " +
      "Pose des questions pour apprendre, approfondir et trouver des ressources ou acteurs de référence.",
    exampleQuestion: 'Comment faire… ?',
    exitCondition:
      "Quand tu as obtenu une réponse satisfaisante à ta question initiale et identifié des ressources ou acteurs de référence utiles.",
    lostWhen: 'not_source',
    headlineMetric: { signal: 'brandIsSource', kind: 'rate' },
    outcomes: {
      win:  'Cité comme source',
      part: 'Cité',
      lose: 'Non cité comme source',
      verb: 'sourcé',
    },
    defaultMaxTurns: 4,
  },
  discover: {
    id: 'discover',
    name: 'Recherche de solution',
    description:
      "Tu es un acheteur ou prescripteur qui explore ses options dans un domaine. " +
      "Pose des questions pour découvrir les acteurs disponibles, comparer les grandes catégories et affiner ta recherche.",
    exampleQuestion: 'Quelles options ai-je ?',
    exitCondition:
      "Quand tu as une liste d'options claires à évaluer ou que tu sais vers quoi te tourner pour approfondir.",
    lostWhen: 'not_cited',
    headlineMetric: { signal: 'dropOutTurn', kind: 'exit_turn' },
    outcomes: {
      win:  'Recommandé',
      part: 'Cité',
      lose: 'Non cité',
      verb: 'recommandé',
    },
    defaultMaxTurns: 6,
  },
  compare: {
    id: 'compare',
    name: 'Comparaison',
    description:
      "Tu es un acheteur ou prescripteur qui compare des options précises pour prendre une décision. " +
      "Pose des questions pour évaluer les alternatives, obtenir une recommandation concrète et trancher.",
    exampleQuestion: 'A ou B pour mon cas ?',
    exitCondition:
      "Quand tu as obtenu une recommandation claire entre les options ou que tu peux prendre une décision argumentée.",
    lostWhen: 'eliminated',
    headlineMetric: { signal: 'eliminatedAtTurn', kind: 'exit_turn' },
    outcomes: {
      win:  'Choisi',
      part: 'Cité',
      lose: 'Éliminé',
      verb: 'choisi',
    },
    defaultMaxTurns: 6,
  },
  review: {
    id: 'review',
    name: 'Avis',
    description:
      "Tu es un acheteur ou prescripteur qui veut savoir ce que l'on pense des acteurs d'un domaine : réputation, " +
      "fiabilité, retours de clients. Pose des questions pour recueillir des avis et te faire une opinion.",
    exampleQuestion: "Qu'en pensent les clients ?",
    exitCondition:
      "Dès que tu t'es fait une opinion argumentée.",
    lostWhen: 'not_cited',
    headlineMetric: { signal: 'dropOutTurn', kind: 'exit_turn' },
    outcomes: {
      win:  'Recommandé',
      part: 'Cité',
      lose: 'Non cité',
      verb: 'cité',
    },
    defaultMaxTurns: 6,
  },
};

/** Resolves an intent by id; throws on an unknown id. */
export function resolveIntent(id: string): ConversationIntent {
  const intent = CONVERSATION_INTENTS[id as ConversationIntentId];
  if (!intent) throw new Error(`Unknown conversation intent: "${id}"`);
  return intent;
}

/**
 * Optional per-persona override of the intent's `exitCondition` (2026-10-03).
 *
 * This is a SOFT criterion: the prose below replaces the intent's own in the
 * persona's system prompt, and the model decides when it is met — nothing in the
 * runtime evaluates it. `maxTurns` remains the only hard stop. The hard, per-turn
 * evaluated version is the deferred end-condition registry (plans §7.7); keep the
 * two apart, and keep `exitConditionOverride` (free text) deferred — a closed list
 * is a different field, not that one.
 */
export type ConversationStopCriterionId =
  | 'clear_reco'
  | 'named_options'
  | 'price_found'
  | 'answer_found'
  | 'full_length';

export const CONVERSATION_STOP_CRITERIA: ConversationStopCriterionId[] = [
  'clear_reco', 'named_options', 'price_found', 'answer_found', 'full_length',
];

/**
 * The prose actually sent to the persona, addressed to it in the second person like
 * every `ConversationIntent.exitCondition`. ≤ 200 chars each.
 *
 * `full_length` is the honest spelling of "never stop early": it still has to tell the
 * persona what to do, because an empty exit condition makes the model invent one.
 */
export const CONVERSATION_STOP_CRITERION_PROSE: Record<ConversationStopCriterionId, string> = {
  clear_reco:
    "Quand tu as obtenu une recommandation claire et argumentée, nommant un acteur précis.",
  named_options:
    "Quand au moins trois options distinctes t'ont été nommées et que tu peux les comparer.",
  price_found:
    "Quand tu as obtenu un prix, un ordre de grandeur tarifaire ou la façon d'obtenir un devis.",
  answer_found:
    "Quand tu as une réponse directe et utilisable à la question que tu posais au départ.",
  full_length:
    "Ne t'arrête pas de toi-même : continue à creuser et à relancer jusqu'au dernier tour.",
};

// ---------------------------------------------------------------------------
// Signal registry (D27)
// ---------------------------------------------------------------------------

/**
 * D27 — one entry in the measurement vocabulary. Ships in v1: it is the index of
 * everything Layer A and Layer B write on a turn, what the metric picker offers, and
 * what the colour ramp reads. `kpiUsable: false` = stored and exported, never a headline.
 * `null` (not `0`) when inapplicable: `narrativeAlignment` on a turn that never mentions
 * the brand is undefined, not zero, and a zero would drag every mean unrecoverably.
 */
export interface ConversationSignal {
  type: 'bool' | 'int' | 'enum' | 'score100';
  source: 'regex' | 'llm';
  definedWhen: 'always' | 'cited' | 'cited_earlier' | 'discusses_brand' | 'enumerated_and_cited';
  kpiUsable: boolean;
  aggregation?: 'rate' | 'mean' | 'distribution';
  /**
   * `'higher'` = a bigger value is better (colour ramp max = good).
   * `'lower'`  = a smaller value is better (citedPosition = 1 is winning).
   * Absent when direction is not meaningful (distribution, non-KPI).
   */
  direction?: 'higher' | 'lower';
  /** Scale hint for the colour ramp. `'rank'`/`'count'` bucket on observed quantiles. */
  scale?: 'rate' | 'score100' | 'ordinal' | 'rank' | 'count';
}

export const CONVERSATION_SIGNALS: Record<string, ConversationSignal> = {
  // ---- Layer A: deterministic, written by the tool before it returns ----
  cited: {
    type: 'bool', source: 'regex', definedWhen: 'always',
    kpiUsable: true, aggregation: 'rate', direction: 'higher', scale: 'rate',
  },
  citedPosition: {
    type: 'int', source: 'regex', definedWhen: 'enumerated_and_cited',
    kpiUsable: true, aggregation: 'mean', direction: 'lower', scale: 'rank',
  },
  brandIsSource: {
    type: 'bool', source: 'regex', definedWhen: 'always',
    kpiUsable: true, aggregation: 'rate', direction: 'higher', scale: 'rate',
  },
  ownDomainCited: {
    type: 'bool', source: 'regex', definedWhen: 'always',
    kpiUsable: true, aggregation: 'rate', direction: 'higher', scale: 'rate',
  },
  urlInText: {
    type: 'bool', source: 'regex', definedWhen: 'always',
    kpiUsable: true, aggregation: 'rate', direction: 'higher', scale: 'rate',
  },
  competitorCount: {
    type: 'int', source: 'regex', definedWhen: 'always',
    kpiUsable: true, aggregation: 'mean', direction: 'lower', scale: 'count',
  },
  // ---- Layer B: LLM judge, written later by partial UpdateItem ----
  recommendationStrength: {
    type: 'enum', source: 'llm', definedWhen: 'cited',
    kpiUsable: true, aggregation: 'rate', direction: 'higher', scale: 'rate',
  },
  sentiment: {
    type: 'enum', source: 'llm', definedWhen: 'discusses_brand',
    kpiUsable: true, aggregation: 'distribution',
  },
  narrativeAlignment: {
    type: 'score100', source: 'llm', definedWhen: 'discusses_brand',
    kpiUsable: true, aggregation: 'mean', direction: 'higher', scale: 'score100',
  },
  eliminated: {
    type: 'bool', source: 'llm', definedWhen: 'cited_earlier',
    kpiUsable: true, aggregation: 'rate', direction: 'lower', scale: 'rate',
  },
  // ---- Non-KPI: stored and exported, never a headline (kpiUsable: false) ----
  sources: {
    type: 'enum', source: 'regex', definedWhen: 'always',
    kpiUsable: false,
  },
  competitorsMentioned: {
    type: 'enum', source: 'regex', definedWhen: 'always',
    kpiUsable: false,
  },
  otherBrandsCited: {
    type: 'enum', source: 'llm', definedWhen: 'always',
    kpiUsable: false,
  },
  observedIntent: {
    type: 'enum', source: 'llm', definedWhen: 'always',
    kpiUsable: false,
  },
};

// ---------------------------------------------------------------------------
// Cell (D26, D32, D48)
// ---------------------------------------------------------------------------

/**
 * D26 — one measurement configuration: (engine × language × zone) + run count.
 * Embedded on the persona — bounded, always read with its parent, never queried alone.
 */
export interface ConversationCell {
  /** Run-path engine id. Never `ai_overview` or `claude` (reject in the DTO). */
  engine: ConversationEngine;
  /** BCP-47-ish language tag, within the tenant's `languages` entitlement. */
  language: string;
  /**
   * D32 — `zoneKeyFrom(suggestion)`: `FR` for a country, `BE-anvers` for a sub-country place.
   * Pattern `/^[A-Z]{2}(-[a-z0-9-]{1,24})?$/`. Entitlement checked on the first 2 chars.
   * I1 step 2: optional — absent means Mondial (no zone sent to engines).
   */
  zone?: string;
  /**
   * D32 — the picked GeoSuggestion stored verbatim as TargetZone.
   * Prompt reads label + countryCode; localizer reads type; UI reads the flag.
   * I1 step 2: optional — absent means Mondial.
   */
  zoneMeta?: TargetZone;
  /** Independent conversations per launch on this cell (≥ 1). Non-uniform is intentional. */
  runs: number;
  /**
   * D40 — the per-cell copy of `startingPrompt`, stored VERBATIM. Absent ⇔ improvise turn 1.
   * Despite the name this is NOT localized: localizing it injected the zone key into the
   * text and dropped brand names (2026-09-29). Zone → `geo.py`, language → system_instruction.
   * Name kept for payload compatibility.
   */
  startingPromptLocalized?: string;
  /**
   * D40 — pinned questions localized for this cell at save time.
   * POSITIONAL, index-aligned with the persona's `scriptedQuestions`.
   * Never filter, sort or reorder this list.
   */
  scriptedQuestionsLocalized?: string[];
}

// ---------------------------------------------------------------------------
// Persona (D1)
// ---------------------------------------------------------------------------

/**
 * D1 — the config object: what the user authors, edits and compares over time.
 * A persona owns its cells[], one intent, one cadence, and an optional first message.
 * One launch fires every cell and every run under one credit reserve.
 */
export interface ConversationPersona {
  tenantId: string;
  brandId: string;
  personaId: string;
  name: string;
  /**
   * Link to the brand-level Persona (Brand Knowledge « Leviers de mesure », 2026-10-11).
   * When set, this row carries NONE of `BRAND_PERSONA_TRAIT_FIELDS` — the dispatcher loads
   * the `BrandPersona` at every run, so an edit is live from the next run. Required when
   * `mode === 'improvised'` on the editor path; optional on mixed/scripted (absent = a
   * neutral buyer). Absent on rows created before the split: those keep their inline traits.
   */
  brandPersonaId?: string;
  /** ≤ 600 chars — who this person is. Legacy inline trait; absent when `brandPersonaId` is set. */
  description?: string;
  intentId: ConversationIntentId;
  /** Legacy inline trait; absent when `brandPersonaId` is set. */
  tone?: ConversationTone;
  ageRange?: string;
  gender?: 'female' | 'male' | 'unspecified';
  budget?: string;
  /** ≤ 5. Pre-filled from BrandStrategy.customerObjections. */
  objections?: string[];
  /** ≤ 5. */
  decisionCriteria?: string[];
  /** D9 — 'unaware' only in v1; the field exists so 'aware' is additive later. */
  brandAwareness: 'unaware';
  /**
   * `compare` personas only — ≤ 5 competitorIds from CompetitorsService.list.
   * 400 if present on learn/discover (an unused field on a saved persona reads as a bug).
   * Rendered as tracked names in the prompt (§3.2).
   */
  comparisonSet?: string[];
  /**
   * Target IDs to use as comparators for `compare` personas.
   * Mirrors `comparisonSet` but uses Cibles target IDs instead of competitor IDs.
   */
  comparatorTargetIds?: string[];
  /**
   * 2..10 — « donne-moi N options ». Reaches the prompt as `list_size`.
   * When unset the persona asks however it likes.
   */
  listSize?: number;
  /**
   * D61 — 'broad': open-ended "quelle est la meilleure marque…" questions.
   * 'focused': explicitly names 1–2 brands when asking comparisons.
   */
  selectionFocus?: ConversationSelectionFocus;
  /** D61 — Domain knowledge level, shapes vocabulary and precision. */
  domainExpertise?: ConversationDomainExpertise;
  /** D61 — Writing register: typos/shortcuts, casual or professional. */
  languageStyle?: ConversationLanguageStyle;
  /** D61 — How much the persona challenges AI answers vs. accepts them. */
  receptiveness?: ConversationReceptiveness;
  /**
   * UI « Premier message (optionnel) ». Unset is the normal (coverage study).
   * When set every run starts identically (variance study). Must pass the no-brand guard.
   * Localized once per cell at save, never at dispatch.
   */
  startingPrompt?: string;
  /**
   * D40 — `'improvised'` is the default; absent field means improvised on pre-existing rows.
   * `startingPrompt` alone is the collapsed form of `'mixed'` with one pinned turn.
   */
  mode: ConversationMode;
  /**
   * D40 — unique on `turn`, 1 ≤ turn ≤ maxTurns, text ≤ CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS.
   * Empty (absent) unless `mode !== 'improvised'` (400 otherwise).
   * In `scripted`: contiguous 1..length, no holes. In `mixed`: holes are the improvised turns.
   * A question naming a tracked brand is a 400.
   */
  scriptedQuestions?: { turn: number; text: string }[];
  /**
   * Optional override of the intent's `exitCondition` prose (2026-10-03). Unset = the
   * intent decides. SOFT — the model judges it; `maxTurns` is still the only hard stop.
   * Ignored on `mode === 'scripted'` (the question list ends the conversation).
   */
  stopCriterion?: ConversationStopCriterionId;
  /**
   * 1 ≤ length ≤ CONVERSATION_MAX_CELLS, unique on cellKey (duplicate = 400, never a merge).
   *
   * TRANSITIONAL (lot I2, shared phase): still REQUIRED here, so this type is the ASSEMBLED
   * shape (what every API response carries). The STORED row is `ConversationPersonaRow`, where
   * `cells` is optional: present ⇔ legacy INLINE layout; a split row (`configVersion` set) has
   * none, its cells are `BPCELL#` items read only through `loadPersonaCellsWith`
   * (utils/conversationConfigStore.ts). The readers of stored rows move to
   * `ConversationPersonaRow` + the loaders in the next I2 phase; then this becomes optional as in
   * the I2 contract.
   */
  cells: ConversationCell[];
  /**
   * Lot I2 (2c): present ⇔ split layout. `${13-digit ms}-${4 base32}`, fixed width, strictly
   * greater than the one it replaces (`nextConfigVersion`). Absent = legacy inline `cells` (C0).
   */
  configVersion?: string;
  /** Split only: `BPCELL#` items under `configVersion` (the loaders' completeness check). */
  cellCount?: number;
  /**
   * Split only: conversationLaunchCost(cells) of `configVersion`, same write as `cellCount`.
   * Legacy rows: absent, derived at read time by `personaRunsPerLaunch` (C0, no backfill).
   */
  runsPerLaunch?: number;
  /** ≤ CONVERSATION_MAX_TURNS. Clamped by tier; defaults to the intent's defaultMaxTurns. */
  maxTurns: number;
  /** Own cadence — NOT a SchedulableAuditType; the sweep drives this, not EventBridge per brand. */
  cadence?: Cadence;
  /**
   * 00b DQ3 — anchor day (1..31) of a legacy per-persona `months` cadence (API / MCP only: the
   * form never sends `cadence`). SET by a write that changes `cadence` to a `months` one (the
   * day of month of the first `nextRunAt` it schedules), kept by a re-write of the same
   * `months` cadence, REMOVEd by a write of another `cadence`. Absent on old rows (C0).
   */
  monthAnchorDay?: number;
  /** IANA timezone for cadence scheduling, e.g. "Europe/Paris". Null = UTC. */
  timezone?: string;
  /** Local hour (HH:mm) for the cadence tick, combined with timezone. */
  scheduleHour?: string;
  status: ConversationStatus;
  /**
   * §7.8 — user-defined tags, ≤ CONVERSATION_MAX_TAGS, each lowercase-kebab ≤ 30 chars.
   * Derived tags (`intent:<id>`, `mode:<mode>`) are never stored here.
   * Absent on personas created before tags shipped; treat as `[]`.
   */
  tags?: string[];
  /** Sweep state. `dueBucket` present ⇔ persona is in the due-index (active + cadence set). */
  nextRunAt?: string;
  dueBucket?: string;
  lastClaimedFor?: string;
  lastLaunchAt?: string;
  lastLaunchId?: string;
  lastLaunchStatus?: string;
  /**
   * C17.4 refusal record: written ONLY by a refused launch, REMOVEd by the write that records
   * a successful launch. `lastBlockedAt` = the refused tick's `scheduledFor` (ISO).
   */
  lastBlockedAt?: string;
  lastBlockReason?: 'quota_exceeded' | 'quota_unavailable' | 'insufficient_credits';
  /**
   * C17.6 — set with `status: 'archived'` by the allowance reconcile when a downgrade pauses the
   * conversation for the plan (`allowance-reconcile.service.ts`); lifted only by « Réactiver ».
   */
  archivedReason?: 'plan_limit';
  createdAt: string;
  updatedAt: string;
}

/**
 * Lot I2 (2c) — the STORED persona row (PK BRAND#<b> · SK PERSONA#<p>): `cells` present ⇔
 * legacy inline layout, absent on a split row (`configVersion`, `cellCount`, `runsPerLaunch`).
 * Read its cells only through `loadPersonaCellsWith`, its runs per launch only through
 * `personaRunsPerLaunch`; never write an assembled `ConversationPersona` back.
 */
export type ConversationPersonaRow = Omit<ConversationPersona, 'cells'> & { cells?: ConversationCell[] };

// ---------------------------------------------------------------------------
// Brand Persona (2026-10-11) — the « Qui est-il ? » card, reusable across conversations
// ---------------------------------------------------------------------------

/**
 * The trait fields owned by a `BrandPersona`. A `ConversationPersona` with
 * `brandPersonaId` set must carry none of them (400 at save); the dispatcher reads them
 * off the linked `BrandPersona` instead. Same caps as before the split
 * (`CONVERSATION_MAX_PERSONA_*`), same no-brand guard on description/objections/criteria.
 */
export const BRAND_PERSONA_TRAIT_FIELDS = [
  'description',
  'tone',
  'ageRange',
  'gender',
  'budget',
  'objections',
  'decisionCriteria',
  'selectionFocus',
  'domainExpertise',
  'languageStyle',
  'receptiveness',
] as const;
export type BrandPersonaTraitField = (typeof BRAND_PERSONA_TRAIT_FIELDS)[number];

/**
 * PK `BRAND#<brandId>` · SK `BPERSONA#<brandPersonaId>` in the conversations table.
 * Counted by the PER-BRAND `brand_personas` gauge (default 100, overridable).
 * Not part of `BrandStrategy`: editing a persona never flips a strategy to `stale`.
 * Delete is refused while any conversation links it.
 */
export interface BrandPersona {
  tenantId: string;
  brandId: string;
  brandPersonaId: string;
  /** ≤ CONVERSATION_MAX_PERSONA_NAME_CHARS — the title searched in the conversation editor. */
  name: string;
  description: string;
  tone: ConversationTone;
  ageRange?: string;
  gender?: 'female' | 'male' | 'unspecified';
  budget?: string;
  objections?: string[];
  decisionCriteria?: string[];
  selectionFocus?: ConversationSelectionFocus;
  domainExpertise?: ConversationDomainExpertise;
  languageStyle?: ConversationLanguageStyle;
  receptiveness?: ConversationReceptiveness;
  /** ≤ CONVERSATION_MAX_TAGS, lowercase-kebab ≤ 30 chars (same rule as conversation tags). */
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Starting intent → the Brand Perception axis whose prompts the editor offers as pinned
 * questions (2026-10-11), the row's rail colour and the Couverture axis. Apprentissage→Autorité,
 * Recherche de solution→Recommandation, Comparaison→Comparaison (with the AXIS_SPLIT_LIVE flip,
 * Image before it), Avis→Image. A UI default only — any axis or free text is allowed.
 */
export const CONVERSATION_INTENT_PROMPT_AXIS: Record<ConversationIntentId, 'authority' | 'referral' | 'narrative' | 'comparison'> = {
  learn: 'authority',
  discover: 'referral',
  compare: 'comparison',
  review: 'narrative',
};

/**
 * I1 step 2 — build a conversation's default display name.
 * The ROW displays the live BrandPersona name + intent; `name` is used only
 * where the persona is unlinked (legacy rows). Old rows keep their typed name.
 */
export function defaultConversationName(personaName: string, intentId: ConversationIntentId): string {
  const intent = CONVERSATION_INTENTS[intentId];
  // Sliced to the name cap: a long persona name + « Recherche de solution » would 400 the save.
  return `${personaName} · ${intent?.name ?? intentId}`.slice(0, CONVERSATION_MAX_PERSONA_NAME_CHARS);
}

// ---------------------------------------------------------------------------
// Sub-run summary (RUN# row, ≤ 8 KB) and extras (RUNX# row, 20–50 KB)
// ---------------------------------------------------------------------------

/**
 * PK PERSONA#<personaId> · SK RUN#<cellKey>#<launchId> — ≤ 8 KB.
 *
 * Budget: `runs[]` at 20 × ~27 scalars ≈ 5 KB; all histograms and counters fit the rest.
 * Nothing unbounded (no per-domain maps, no prose, no per-turn structures) may ever land here.
 */
export interface ConversationSubRun {
  tenantId: string;
  brandId: string;
  personaId: string;
  /** D51/D54 — the launch identifier. NEVER `runId` (README §0.2). */
  launchId: string;
  /** Denormalized: no reader should split the key to recover these. */
  engine: string;
  language: string;
  /** I1 step 2: optional — absent = Mondial (no zone). */
  zone?: string;
  cellKey: string;
  status: ConversationRunStatus;
  trigger: 'cron' | 'manual';
  /** The claimed scheduledFor tick — the credit reserve's idempotency anchor. */
  triggeredFor: string;
  /**
   * Config SNAPSHOT at dispatch. Never re-resolve from the persona when reading:
   * the persona may have been edited since the launch was created.
   */
  config: {
    intentId: ConversationIntentId;
    maxTurns: number;
    runs: number;
    /** Already localized for this cell. */
    startingPrompt?: string;
    mode: ConversationMode;
    /**
     * D40 — localized questions as asked, not the original config.
     * Mode + these questions are the only way to tell a scripted run from improvised after the fact.
     * @deprecated The dispatcher now sends `scriptedTurns` (I1 step 2); old rows keep this field.
     */
    scriptedQuestionsLocalized?: { turn: number; text: string }[];
    /**
     * I1 step 2 — distinct turn numbers that have scripted options, ascending.
     * `[]` = improvised; absent on legacy rows (use scriptedQuestionsLocalized path).
     */
    scriptedTurns?: number[];
    /** Intent's exitCondition prose, as told to the persona. */
    exitCondition: string;
    brandAwareness: 'unaware';
    /**
     * D41 — hash the matcher ACTUALLY used (from the result, not the request).
     * Provenance only; roll-ups annotate on a mismatch but never filter.
     */
    targetsHash: string;
    targetsHashDrifted?: boolean;
    /**
     * Lot I2 (2c) — the persona's `configVersion` read at claim (absent for a legacy inline
     * row). The dispatcher reads THIS version's `BPCELL#` / `BPOPT#`, never the current one,
     * so a save during a launch never mixes versions.
     */
    configVersion?: string;
  };
  expected: number;
  succeeded: number;
  failed: number;

  /**
   * Sufficient statistics (§2.8). ADDITIVE: merge by addition.
   * Never a rate — a mean of rates is not a rate of sums.
   * Derived rates are written once as headlines so no reader needs to divide.
   */
  stats: {
    /** Runs that produced a measurable conversation (excludes engine failures). */
    n: number;
    /** Engine failures — EXCLUDED from n and every denominator. */
    cX: number;
    /** Outcome 1 — no cited turn at all in the run. */
    cNever: number;
    /**
     * cDrop[t] = runs dropped AT turn t: the last cited turn was t-1 and t ≤ turnsCompleted.
     * Length maxTurns; indices 0 and 1 are unused (can't drop at turn 0 or 1 while cited
     * at turn 0). The turns between first and last citation are NOT recoverable from the code.
     */
    cDrop: number[];
    /** Outcome M — cited on the final completed turn, not recommended. */
    cKept: number;
    /** Outcome R — recommended. */
    cReco: number;
    /**
     * D36 — runs cited at turn 1. The ONLY source of t1.
     * NOT derivable from cNever: a run absent at turn 1 and cited at turn 3 is neither cNever
     * nor counted here — so `(n − cNever) / n` over-counts and is the wrong formula.
     */
    cT1: number;
    /**
     * presence = sumPresentTurns / sumTurns.
     * `sumTurns` is the sum of COMPLETED turns (run length varies with `end_conversation`),
     * NOT `maxTurns × n`. The name is deliberate; a reader who "fixes" it inflates the denominator.
     */
    sumPresentTurns: number;
    sumTurns: number;
    /**
     * D23 — turns with ≥ 1 owned match via: 'source'.
     * Same `sumTurns` denominator as presence.
     * `brandIsSourceRate = sumSourceTurns / sumTurns` — the `learn` intent's HEADLINE metric.
     * Must be a counter (not derived from the outcome code) so the rate is additive across grains.
     */
    sumSourceTurns: number;
    /** D42 — over turns mentioning ≥ 1 brand (the share-of-voice denominator). */
    mentionsOwn: number;
    mentionsAll: number;
    /** D42 — runs where the runtime brand guard fired (induced turn). */
    cInduced: number;
    /**
     * D53 — turns with ≥ 1 owned match of TARGET_CATEGORIES[i].
     * Same `sumTurns` denominator as presence.
     * Σ_i ≥ sumPresentTurns (a turn citing both produit AND marque counts once in presence
     * but once per category).
     */
    presentTurnsByCategory: number[];
    /**
     * Layer B — turns where the brand was cited, keyed by the turn's observed intent
     * (learn | discover | compare | other). Same D42 denominator as presence.
     * Absent on rows written before Layer B intent classification was added.
     */
    presentTurnsByObservedIntent?: Record<string, number>;
    /**
     * Total non-induced turns per observed intent (same denominator as `sumTurns`).
     * Paired with `presentTurnsByObservedIntent`: rate = present / turns.
     */
    turnsByObservedIntent?: Record<string, number>;
    /**
     * Prompt verdict (§3.12, 2026-11-02) — Σ `promptScore.score` over the non-induced
     * completed turns that HAVE a score (an unscored turn adds nothing, never a 0), and
     * how many such turns. Mean = sum / count, folded by the rollup into `scoreSpark`.
     * Absent on rows written before 2026-11-02 (= not measured, never 0).
     */
    sumPromptScore?: number;
    promptScoredTurns?: number;
    /** Same turns, counted per `promptIntent` (the five prompt intents) → `intentSpark`. */
    turnsByPromptIntent?: Record<string, number>;
    /** Set-global predicate needed by the roll-up to bucket by intent. */
    intentId: ConversationIntentId;
  };

  // Derived headlines — written once so no reader needs to divide (§2.8).
  /** `null` when n === 0, never 0. */
  presence: number | null;
  /** `cT1 / n`. `null` when n === 0. */
  t1: number | null;
  /** `mentionsOwn / mentionsAll`. `null` when mentionsAll === 0. */
  shareOfVoice: number | null;
  /** Count of runs with an induced turn (display counter). */
  mentionsInduites: number;
  /** Length maxTurns. The 7-column histogram is composed client-side from this. */
  dropOutHistogram: number[];
  neverCited: number;
  meanDropOutTurn: number | null;
  /** Runs still cited on the final turn but conversation ended early (`partial`). */
  censoredCount: number;
  meanScoreByTurn: (number | null)[];
  citedRate: number;
  /** `sumSourceTurns / sumTurns` — per-TURN rate on the same denominator as presence. */
  brandIsSourceRate: number;
  /** Rate of runs where `endedBy === 'agent_says_done'` — intent fulfilled. */
  intentFulfilledRate: number;
  endedByCounts: Record<ConversationEndedBy, number>;
  sourcesTruncated: { domains: boolean; urls: boolean; targets: boolean };
  domainCount: number;
  urlCount: number;

  /**
   * Per-run headline scalars so the matrix renders in one GetItem per cell.
   * ORDERED LIST, not a bag — never sort, compact or drop an 'x' entry.
   * ~27 scalars × 20 runs ≈ 5 KB of the 8 KB budget.
   */
  runs: {
    idx: number;
    status: 'completed' | 'partial' | 'failed';
    /** D36 — the single field the matrix grid colour-codes. */
    outcome: ConversationOutcome;
    /** cited turns / turnsCompleted — NOT derivable from `outcome` (see D36 for why). */
    presence: number;
    /**
     * D40 — `'script_done'` means scripted mode ran out of questions.
     * NOT folded into `'max_turns'`: the two diagnoses call for different actions.
     */
    endedBy: ConversationEndedBy;
    induced: boolean;
    durationMs: number;
    /** Actual credits billed (a partial run bills fewer than maxTurns × 2). */
    credits: number;
    turnsCompleted: number;
    lastCitedTurn: number | null;
    /** Ended partial while still cited — excluded from meanDropOutTurn. */
    censored: boolean;
    endedByAgent: boolean;
    /** ≤ 200 chars, untrusted display text, never fed to the judge. */
    endReason?: string;
    /** sha1 of the normalized turn-1 message (§3.15). */
    openerHash: string;
    // Selectable per-run metrics (D27), aggregated over this run's turns:
    citedTurns: number;
    meanPosition: number | null;
    meanAlignment: number | null;
    meanCompetitorCount: number | null;
    sourceTurns: number;
    eliminatedAtTurn: number | null;
    finalPosition: number | null;
    positionDrift: number | null;
    ownSourceShare: number | null;
    /** D53 — TARGET_CATEGORIES indexes cited at least once, in ascending order. */
    cats: number[];
  }[];
  /** §3.15 — distinct turn-1 openers / totalRuns. */
  distinctOpeners: number;

  analysisStatus: ConversationAnalysisStatus;
  reservedCredits: number;
  committedCredits?: number;
  startedAt: string;
  finishedAt?: string;
  /** D15 — exempt from the retention cascade. */
  pinned?: boolean;
}

// ---------------------------------------------------------------------------
// Launch queue (lot I2, step 8d)
// ---------------------------------------------------------------------------

/**
 * One cell of a `ConversationLaunchMessage`: ONLY what the launch consumer writes. The
 * dispatcher reads the full cell itself (`BPCELL#` of `config.configVersion`, or the inline
 * row), so no `startingPromptLocalized` / `scriptedQuestionsLocalized` / `zoneMeta` ever
 * rides the queue.
 */
export interface ConversationLaunchCell {
  engine: ConversationEngine;
  language: string;
  /** Absent = Mondial. */
  zone?: string;
  runs: number;
  /**
   * The producer's EXACT agency hold for this cell (runs × maxTurnsResolved × 2, the amount
   * its per-cell reserve took); 0 self-serve. Written verbatim on the `RUN#` summary
   * (`reservedCredits`): `counter.py` skips the commit when it is 0. Required, never defaulted.
   */
  reservedCredits: number;
}

/**
 * The `conversation-launch` queue message. A launch is sent as one or more of these, each
 * carrying a chunk of the launch's launched cells (`launchCells`), chunked by BYTES, never by
 * cell count: cells are appended while the JSON stays ≤ 200 KB, and messages are packed into a
 * `SendMessageBatch` call while the call stays ≤ 256 KB and ≤ 10 entries.
 */
export interface ConversationLaunchMessage {
  tenantId: string;
  brandId: string;
  personaId: string;
  launchId: string;
  scheduledFor: string;
  source: 'sweep' | 'run_now';
  /** ms, the producer's clock just before its first agency hold (self-serve: before LNCH#). */
  reservedAt: number;
  /** The `RUN#` config snapshot, computed by the producer (incl. `scriptedTurns`, `configVersion`). */
  config: ConversationSubRun['config'];
  cells: ConversationLaunchCell[];
}

/**
 * PK PERSONA#<personaId> · SK RUNX#<cellKey>#<launchId> — 20–50 KB, read on click only.
 *
 * Named `RUNX#` so `begins_with(SK, 'RUN#')` does not match it (D50).
 * Written BEFORE the summary: an orphan RUNX# is collectable garbage; a summary
 * without its extras renders an empty drawer.
 */
export interface ConversationSubRunExtras {
  tenantId: string;
  brandId: string;
  personaId: string;
  launchId: string;
  cellKey: string;
  /** D44 — turn counts by stage: Exploration (turn 1), Évaluation (2–4), Décision (5–6). */
  stageTurns: Record<ConversationStage, number>;
  /**
   * Top 20 domains. Invariant `cit ≥ turns ≥ runs`:
   * `turns` is incremented once per turn; `runs` once per run — recoverable only at write time.
   * `stage` is the modal stage; ties resolve toward the later stage.
   */
  topDomains: {
    domain: string;
    cit: number;
    turns: number;
    runs: number;
    stage: ConversationStage;
    ownDomain: boolean;
    /** Injected server-side by getCellSources (§2.10). Absent on raw RUNX# rows. */
    faviconUrl?: string;
    /** Injected server-side by getCellSources (§2.10). Null = type inconnu, never dropped. */
    category?: DomainClassification | null;
  }[];
  /** Top 50 URLs. */
  topUrls: {
    url: string;
    domain: string;
    cit: number;
    turns: number;
    runs: number;
    stage: ConversationStage;
  }[];
  /**
   * D53 — top 30 cibles by `cit`. `value` is the folded probe for UI highlighting.
   * Display label is resolved client-side from the Cibles manifest by `targetId` —
   * never stored (a rename would freeze a stale label).
   */
  topTargets: {
    targetId: string;
    category: TargetCategory;
    /** Folded probe; for pairs: `'a|b'`. */
    value: string;
    rootMarqueId?: string;
    cit: number;
    turns: number;
    runs: number;
  }[];
  /**
   * Residuals — what the caps threw away. NEVER omit: `Σ top.cit + residual.cit` is the true total.
   */
  residualDomains: { count: number; cit: number };
  residualUrls: { count: number; cit: number };
  residualTargets: { count: number; cit: number };
  /**
   * GAP-2 — Σ `cit` per TARGET_CATEGORIES index for this sub-run, computed PRE-cap.
   * Exact, unlike anything derivable from `topTargets` + `residualTargets`.
   * Feeds the brand's `TDAY#.citByCategory` (block ⑧ tab 1) via the rollup's launch fold.
   * Absent on rows written before 2026-10-05.
   */
  citByCategory?: number[];
  /**
   * GAP-2 — the cited `targetId`s of this sub-run, grouped by category, ranked by `-cit`
   * and capped in TOTAL at CONVERSATION_MAX_CITED_TARGET_IDS.
   *
   * Lives here and not on `RUN#` because `RUN#` is asserted under a 15 KB ceiling by
   * tests/test_size_assertions.py and already measures ~10 KB. Read by the rollup with a
   * ProjectionExpression, so the row's other 20–50 KB never cross the wire for this.
   * Absent on rows written before 2026-10-05.
   */
  citedTargets?: Partial<Record<TargetCategory, string[]>>;
  /** True when the cap above dropped ids — makes `TDAY#.distinctByCategory` a floor for that day. */
  citedTargetsTruncated?: boolean;
  /**
   * D42/D43 — brand → turns mentioned. Unbounded in untracked brands; kept here (not on RUN#)
   * because it is the source for the « nouvel entrant » chip.
   */
  mentionCounts: Record<string, number>;
  /**
   * D43 — Σ rank and count of turns with a readable rank, per brand.
   * Mean = `sumRank / ranked` at read time. Unreadable ranks contribute to neither term.
   */
  rankStats: Record<string, { sumRank: number; ranked: number }>;
}

// ---------------------------------------------------------------------------
// Conversation turn (§2.6) — the export grain, retained 90 days
// ---------------------------------------------------------------------------

/**
 * Reduced TargetMatch persisted on a turn row, in the search's first-appearance order.
 * Dropped: rankInOwner, rankGlobal, tags. (`position` was dropped too until 2026-11-02.)
 */
export interface ConversationTurnMatch {
  targetId: string;
  ownerId: string;
  rootMarqueId?: string;
  category: TargetCategory;
  field: string;
  kind: 'text' | 'url' | 'pair';
  /** Folded value. For pairs: `'a|b'`. Use for highlighting, not position. */
  value: string;
  tier: 'identifie' | 'probable';
  via: 'text' | 'source';
  /** POSITIONAL index into `sourceLinks`. Present ⇔ via === 'source'. */
  sourceIndex?: number;
  /** Every source the signal matched (`TargetMatch.sourceIndexes`); absent before 2026-11-02. */
  sourceIndexes?: number[];
  /**
   * Offset of the first occurrence in the FOLDED answer — for ordering « which target came
   * first », never for highlighting (see `TargetMatch.position`). `null` on a url match;
   * absent before 2026-11-02.
   */
  position?: number | null;
  /**
   * Present ⇔ kind === 'pair': the matcher's `_gap` for the rung this pair matched
   * at (characters BETWEEN the halves — 80 for a street, 25 for a city).
   *
   * Carried so a consumer can REPRODUCE the co-occurrence test before painting half
   * `a`: an ungated pair half claims a match the matcher never made (targets.md
   * §11.1). A highlighter that gets `value` without `window` must drop the highlight,
   * not paint it — rows written before 2026-09-25 have no `window`.
   */
  window?: number;
}

/**
 * PK RUN#<launchId>#<cellKey> · SK CONV#<idx:3>#TURN#<nn:2>
 *
 * The export grain and the click-to-open transcript. Retained 90 days per cell (§5.8).
 * Layer A fields are written by the tool before it returns (always present).
 * Layer B fields are written later by a partial UpdateItem (`undefined` = pending, `null` = n/a).
 */
export interface ConversationTurn {
  launchId: string;
  engine: string;
  runIndex: number;
  /** 1-based. */
  turn: number;
  personaMessage: string;
  engineResponse: string;

  // ---- Layer A: deterministic, written by the tool before it returns ----

  /** D41 — true ⇔ ≥ 1 match on an owned owner (either tier, via text or source). */
  cited: boolean;
  /**
   * Reduced TargetMatch list. Capped at CONVERSATION_MAX_TURN_MATCHES.
   * POSITIONAL arrays `sources`/`sourceLinks` are the index these match against.
   */
  targetMatches: ConversationTurnMatch[];
  targetMatchesTruncated: boolean;
  targetWarnings?: string[];
  /**
   * D53 — TARGET_CATEGORIES indexes with ≥ 1 owned match.
   * From the PRE-CAP `totalsByCategory`, never from the capped `targetMatches` list.
   */
  citedCategories: number[];
  /** Rank in an enumerated list, when the answer is one. */
  citedPosition?: number;
  /**
   * D43 — brand → 1-based rank in an enumerated list; `null` when named in prose
   * with no readable enumeration. Never coerce null to last — that invents a demotion.
   */
  lanes: Record<string, number | null>;
  /** D44 — written with Layer B (requires observedIntent). */
  stage?: ConversationStage;
  /** Any owned match with via === 'source'. */
  brandIsSource: boolean;
  /** Any owned match with via === 'source' AND kind === 'url'. */
  ownDomainCited: boolean;
  /**
   * POSITIONAL citation arrays — index-aligned to inline `[n]` markers.
   * NEVER dedupe, sort or filter either list. `sourceLinks` IS the `urls` sent to targets-search.
   */
  sources: string[];
  sourceLinks: string[];
  competitorsMentioned: string[];
  competitorCount: number;
  competitorSourceDomains: string[];
  urlInText: boolean;
  searchCount: number | null;
  truncated: boolean;
  responseTokens: number;
  latencyMs: number;
  /**
   * D42/D22 — the runtime brand guard matched the persona's own outgoing message.
   * When true, this turn AND every turn after it in the run are excluded from
   * presence and share of voice (the contamination persists in the engine's history).
   */
  induced: boolean;

  // ---- Layer B: LLM judge, written later by partial UpdateItem ----
  // null = not applicable to this turn; undefined = analysis pending.

  sentiment?: 'positive' | 'neutral' | 'negative' | 'mixed' | null;
  /** 0..100. `null` when the answer never discusses the brand (not 0). */
  narrativeAlignment?: number | null;
  recommendationStrength?: 'none' | 'mentioned' | 'considered' | 'recommended' | null;
  /** Explicitly ruled out — the `compare` exit trigger. `null` when not applicable. */
  eliminated?: boolean | null;
  otherBrandsCited?: string[];
  observedIntent?: ConversationIntentId | 'other';
  /** Inline suggestion — never a Task in v1. */
  recommendation?: string;

  // ---- Prompt verdict: the Prompt Studio classifier + judge, run per turn by analysis.py ----
  // Absent = not judged yet (pending, skipped by the time budget, or a run before 2026-11-02).

  /**
   * The persona message's intent, from the prompt classifier's call 1 (the same TypeSafe
   * question as `PromptClassifierService`). Absent when unclassified or below its threshold.
   */
  promptIntent?: BrandQueryIntent;
  /** The prompt judge's verdict on this turn, on the axis `promptIntent` maps to. */
  promptScore?: ConversationTurnPromptScore;

  analysisStatus: 'pending' | 'ready' | 'failed';
  turnStatus: 'completed' | 'failed';
  /** Ended `partial` while still cited — excluded from meanDropOutTurn. */
  censored: boolean;
  createdAt: string;
}

/**
 * `ConversationTurn.promptScore` — `scorer.score_response` (vendored from Brand Perception)
 * on (personaMessage, engineResponse). Axis by intent: learn → authority, recommend →
 * referral, describe | reputation → narrative, compare → comparison.
 */
export interface ConversationTurnPromptScore {
  axis: 'authority' | 'referral' | 'narrative' | 'comparison';
  rule: 'rubric_v0' | 'compare_v2';
  /** 0..100. ABSENT when unscored — never a fallback 0 (see `unscoredReason`). */
  score?: number;
  /**
   * `judge_failed` = no usable verdict; `not_ranked` = a comparison turn with no position for
   * the brand (it compares other brands, or names it without ranking it).
   */
  unscoredReason?: 'judge_failed' | 'not_ranked';
  /** Brand named in the answer text (prompt semantics: `scoring.is_brand_cited`). */
  named: boolean;
  /** Brand domain among the turn's cited sources (`ConversationTurn.brandIsSource`). */
  brandIsSource: boolean;
  sentiment?: 'positive' | 'neutral' | 'negative';
  recommended?: boolean;
  /** 1-based rank: the recommended list (referral) or the compared brands (comparison). */
  position?: number;
  total?: number;
  /** The ordered list the position is read from, ≤ 10 names. */
  rankedList?: string[];
  /** narrative only. */
  alignment?: 'strong' | 'partial' | 'weak' | 'misaligned';
  alignmentReason?: string;
}

// ---------------------------------------------------------------------------
// Rollup shapes (D52)
// ---------------------------------------------------------------------------

/**
 * The ADDITIVE subset of `stats` — everything except `intentId`.
 * Derived by `Omit` on purpose: widening `ConversationSubRun.stats` with a new
 * counter automatically widens day buckets and both roll-ups with no new key,
 * Query or row. What must NOT be added here is a rate: the merge is a sum.
 */
export type ConversationCounters = Omit<ConversationSubRun['stats'], 'intentId'>;

/**
 * `[n, sumPresentTurns, sumTurns, cReco]` — a 4-tuple, not an object.
 * ≤ CONVERSATION_MAX_CELLS (300) per launch; the compact form keeps ledger rows small
 * (`PLAUNCH#.byCell` at 300 cells ≤ 32 KB, item-size.spec.ts).
 */
export type ConversationCellTuple = [number, number, number, number];

export interface ConversationDayBucket {
  /** launchIds already folded. A merge whose id is already present is a NO-OP. */
  launches: string[];
  all: ConversationCounters;
  /**
   * cellKey → 4-tuple. `byEngine` / `byZone` at any grain are SUMS over this
   * (engine = first key component, zone = third). Never stored separately.
   */
  byCell: Record<string, ConversationCellTuple>;
}

/**
 * The persona's daily ledger, aggregated at read time from PLAUNCH# rows.
 * ~300 B/day at 2 cells, ~800 B/day at 12, ~20 KB at 300; a day with no launch has no key (a gap).
 * The day key is the UTC day of `launchTs(launchId)` — the CLAIMED tick, not the
 * finalizer's clock (so a launch closing after midnight dedupes against the right bucket).
 *
 * Write path (additive, post-2026-09-29): each launch writes one idempotent
 * PK=PERSONA#<id>, SK=PLAUNCH#<dayKey>#<launchId> row with TTL=180 days.
 * The rollup Lambda aggregates all PLAUNCH# rows in memory on each launchComplete.
 * No RMW, no version conflicts, no OCC loop.
 *
 * The `version` field is no longer an OCC token; it holds the count of PLAUNCH#
 * rows at aggregation time (monotonically non-decreasing, safe for downstream
 * code that reads it without writing it).
 */
export interface ConversationPersonaLedger {
  tenantId: string;
  brandId: string;
  personaId: string;
  /** Denormalized so the brand job never needs to read a PERSONA# row. */
  name: string;
  intentId: ConversationIntentId;
  /** 'YYYY-MM-DD' (UTC) → bucket. A gap is absent, never a zero-valued entry. */
  days: Record<string, ConversationDayBucket>;
  /** Optimistic-lock version for the GetItem → PutItem conditional write. */
  version: number;
  lastLaunchId: string;
  updatedAt: string;
}

/**
 * One window's aggregated numbers. Repeated three times (7d / 30d / 90d) on every
 * roll-up row. Computed by ONE function `sliceWindow()` in `packages/shared` (D58).
 */
export interface ConversationWindowSlice {
  n: number;
  /** `null` when n === 0, never 0. */
  presence: number | null;
  t1: number | null;
  shareOfVoice: number | null;
  /**
   * D23 — `sumSourceTurns / sumTurns`.
   * On the slice (not left to the browser) because `PSUM#` carries no `stats` field:
   * the Marque view's personas table is the one place a `learn` persona's headline is
   * rendered without access to the full stats.
   */
  sourceRate: number | null;
  outcome: { win: number; part: number; lose: number };
  /**
   * D53 — `presenceByCategory[i]` = sumPresentTurnsByCategory[i] / sumTurns for TARGET_CATEGORIES[i].
   * `null` when n === 0; a true `0` when measured and the category never matched.
   */
  presenceByCategory: (number | null)[];
  prev: {
    presence: number | null;
    t1: number | null;
    shareOfVoice: number | null;
    sourceRate: number | null;
  } | null;
}

/**
 * PK PERSONA#<personaId> · SK ROLLUP — ≤ 32 KB.
 * The only roll-up row read on every persona page load.
 * Cap `lastLaunch` fields AFTER the cross-cell fold (never before):
 * top 20 domains, top 30 cibles, tracked + top-10 untracked brands.
 *
 * This is the ASSEMBLED shape (every API response, and the stored row when written inline).
 * Lot I2 (2c): a pass with more than ROLLUP_INLINE_MAX_CELLS per-cell entries stores
 * `ConversationPersonaRollupSplitRow` + one `ConversationRollupCell` per cell instead; every
 * reader goes through `loadRollupCellsWith` (utils/conversationConfigStore.ts), which rebuilds
 * this shape exactly.
 */
export interface ConversationPersonaRollup {
  tenantId: string;
  brandId: string;
  personaId: string;
  windows: Record<
    ConversationWindow,
    ConversationWindowSlice & {
      /** The drop-out histogram, cInduced, dropAvg — for the persona view's stats blocks. */
      stats: ConversationCounters;
      /**
       * ≤ CONVERSATION_MAX_CELLS cells (plus cells an edit removed that the window still
       * holds); inline on the row only up to ROLLUP_INLINE_MAX_CELLS, else in `RCELL#` items.
       * A cell with no launch in the window is present with `n: 0`
       * so the matrix shows « non mesuré » rather than being absent.
       */
      byCell: {
        cellKey: string;
        engine: string;
        language: string;
        zone: string;
        n: number;
        presence: number | null;
        t1: number | null;
        /** Per-cell outcome counters written by the rollup step. Absent on rows written before this field was added. */
        stats?: ConversationCounters;
      }[];
    }
  >;
  /**
   * Per cell: last ≤ 30 days with data, newest first.
   * `launches` is omitted (removed 2026-09-28 — embedding it inside every cell × day entry
   * blew the 400 KB DDB limit at 42 launches/day × 12 cells × 30 days).
   */
  trend: Record<string, { d: string; launches?: string[]; n: number; presence: number | null }[]>;
  /**
   * day (`YYYY-MM-DD`) → that day's NEWEST launchId, last ≤ 30 days. The day
   * navigator's only source of launch ids since `launches` left `trend` above.
   *
   * One id per day at the top level, not a list per (cell × day): that is the whole
   * difference between ~1.2 KB and the 369 KiB that blew the item limit. The page
   * collapses a day to one id anyway (`sort()[0]`, newest first per D46).
   *
   * Optional: rows written on 2026-09-28 between the `trend.launches` removal and
   * this field carry NEITHER, which is why the page falls back to
   * `persona.lastLaunchId` for the newest day rather than showing every launch-scoped
   * panel as « pas encore consolidé ».
   */
  launchByDay?: Record<string, string>;
  /**
   * The LAST launch's source aggregates, folded from its ≤ CONVERSATION_MAX_CELLS RUNX# rows
   * (chunk by chunk, never all held at once).
   * Labelled « dernier lancement » not « 7 jours »: a window-wide top-20 is not additive.
   */
  lastLaunch: {
    launchId: string;
    at: string;
    /** Re-capped at 20 after the cross-cell fold. */
    topDomains: ConversationSubRunExtras['topDomains'];
    /** Re-capped at 30 after the fold. */
    topTargets: ConversationSubRunExtras['topTargets'];
    stageTurns: Record<ConversationStage, number>;
    /** Tracked brands + top 10 untracked, re-capped after the fold. */
    mentionCounts: Record<string, number>;
    residualDomains: { count: number; cit: number };
    residualTargets: { count: number; cit: number };
    residualBrands: { count: number; turns: number };
  };
  computedAt: string;
}

/** One entry of `ConversationPersonaRollup.windows[w].byCell`. */
export type ConversationRollupByCellEntry =
  ConversationPersonaRollup['windows'][ConversationWindow]['byCell'][number];

/**
 * Lot I2 (2c) — the stored `ROLLUP` row of a SPLIT pass (≤ 32 KB at any cell count): windows
 * WITHOUT `byCell`, no `trend`, plus a pointer to its `RCELL#` set. The layout follows the
 * LAUNCH (the pass's per-cell entry count), never the persona row. Never returned by an API.
 */
export type ConversationPersonaRollupSplitRow = Omit<ConversationPersonaRollup, 'windows' | 'trend'> & {
  windows: Record<
    ConversationWindow,
    Omit<ConversationPersonaRollup['windows'][ConversationWindow], 'byCell'>
  >;
  /** `<launchId>#<passId>` — the `RCELL#` set this row names (`rollupCellSetId`). */
  cellSetId: string;
  /** `RCELL#` items in that set (the loader's completeness check). */
  cellCount: number;
};

/** Either stored `ROLLUP` layout; `cellSetId` present ⇔ split. */
export type ConversationPersonaRollupRow = ConversationPersonaRollup | ConversationPersonaRollupSplitRow;

/**
 * Lot I2 (2c) — PK PERSONA#<personaId> · SK RCELL#<launchId>#<passId>#<cellKey>, ≤ 16 KB.
 * One cell's rollup results for a split pass: its `byCell` entry per window (minus the cell
 * identity, carried once here) and its `trend` points. `pos` = the entry's index in that
 * window's `byCell` array, so the assembled array keeps the writer's order exactly.
 * Rewritten whole per pass, never retracted; a displaced set is deleted by the next pass.
 */
export interface ConversationRollupCell {
  cellKey: string;
  engine: string;
  language: string;
  zone: string;
  windows: Partial<Record<
    ConversationWindow,
    Omit<ConversationRollupByCellEntry, 'cellKey' | 'engine' | 'language' | 'zone'> & { pos: number }
  >>;
  /** `ConversationPersonaRollup.trend[cellKey]`; absent when the cell has no point. */
  trend?: ConversationPersonaRollup['trend'][string];
}

/**
 * PK BRAND#<brandId> · SK PSUM#<personaId> — ≤ 1 KB.
 * One row per persona; the « Personas » table query reads 100/page (≤ 1 page at the 50-persona cap).
 * Carries the 14-day `spark` that shifts with the clock, so it is rewritten on every daily brand pass
 * even when nothing launched.
 */
export interface ConversationPersonaSummary {
  tenantId: string;
  brandId: string;
  personaId: string;
  name: string;
  intentId: ConversationIntentId;
  windows: Record<
    ConversationWindow,
    ConversationWindowSlice & {
      /**
       * D48 — per-engine presence for the « Moteurs » column. `presence: null` =
       * measured no turns; an ABSENT key = the engine is not tracked at all.
       *
       * This was typed `Record<string, number | null>` until 2026-10-03, but
       * `buildByEngineForWindow` in the rollup Lambda has always written the
       * `{ n, presence }` pair (same shape as `ConversationBrandRollup`). The wrong
       * type hid two live bugs: the personas table rendered `ep * 100` as **NaN %**,
       * and `getDashboard`'s PSUM fallback pushed the object into a numeric mean.
       * Fixed by making the type match the writer, which is also the shape the API
       * re-emits.
       */
      byEngine: Record<string, { n: number; presence: number | null }>;
    }
  >;
  /** 14 daily presences, newest LAST, `null` on a day with no launch. */
  spark: (number | null)[];
  /**
   * 14 daily outcome splits, newest LAST, index-for-index with `spark` — `null` on a
   * day with no launch (2026-10-03). Derived from the same day buckets as `spark`
   * (`win = cReco`, `part = cKept - cReco`, `lose = n - cKept`, exactly as
   * `countersToSlice`), so it costs nothing to produce and backfills retroactively:
   * the rollup recomputes the whole ledger from the PLAUNCH# rows on every launch.
   *
   * It is a per-DAY series, not per-run: a day holding two launches is one column.
   * That is deliberate — it keeps the column index-aligned with the 14-day presence
   * sparkline beside it, which is the only way the two read as one story.
   *
   * Optional: rows written before this field carry none, and the personas table falls
   * back to the window-level outcome bar rather than rendering an empty chart.
   */
  outcomeSpark?: ({ win: number; part: number; lose: number } | null)[];
  /**
   * 14 daily MEAN prompt scores (0..100), newest LAST, index-aligned with `spark`
   * (2026-11-02) — `sumPromptScore / promptScoredTurns` of the day's bucket; `null` on a
   * day with no scored turn (no launch, or one before the feature). Optional: older rows.
   */
  scoreSpark?: (number | null)[];
  /**
   * 14 daily turn counts per `promptIntent`, index-aligned with `spark` (2026-11-02);
   * `null` on a day with no classified turn. The list page folds the five prompt intents
   * into its four buckets. Optional: older rows.
   */
  intentSpark?: (Record<string, number> | null)[];
  lastLaunchId: string;
  computedAt: string;
}

/**
 * PK BRAND#<brandId> · SK ROLLUP#<intentId> — one row per intent, all windows.
 * Written ONLY by the brand job (D52 single-writer). Never touched by the persona step.
 */
export interface ConversationBrandRollup {
  tenantId: string;
  brandId: string;
  intentId: ConversationIntentId;
  windows: Record<
    ConversationWindow,
    {
      /** For the count blocks (outcome bar, cInduced, histogram). NOT the source of presence. */
      stats: ConversationCounters;
      /**
       * D39 — UNIFORM MEAN over the intent's personas with n > 0 of each persona's own
       * presence. NOT Σ/Σ across personas (that would weight by run count, not by persona).
       * Write this comment next to the code: it is the one line a future reader will "fix".
       */
      presence: number | null;
      t1: number | null;
      shareOfVoice: number | null;
      /** D23 — same uniform mean convention. Hero number on `ROLLUP#learn`. */
      sourceRate: number | null;
      prev: {
        presence: number | null;
        t1: number | null;
        shareOfVoice: number | null;
        sourceRate: number | null;
      } | null;
      /** D37 — personas with n > 0 in this window. */
      personaCount: number;
      /** D37 — how many personas short of CONVERSATION_MIN_COVER (0 at or above). */
      coverMissing: number;
      /**
       * « Détail du score » engine tab. Sums over ledgers' byCell, then uniform-meaned
       * per persona. ≤ 4 engines.
       */
      byEngine: Record<
        string,
        { n: number; personaCount: number; presence: number | null; prevPresence: number | null; won: number }
      >;
      /**
       * « Détail du score » zone tab. Zones capped at 25 by n.
       */
      byZone: Record<
        string,
        { n: number; personaCount: number; presence: number | null; prevPresence: number | null; won: number }
      >;
      zonesTruncated: boolean;
      /** D53 — uniform mean of each persona's presenceByCategory[i]. */
      byCategory: (number | null)[];
      /**
       * D52 — four ranked « Top 10 » orders written by the brand job (so the Marque view
       * is one Query flat in persona count). The browser merges in-scope intents' lists
       * and re-cuts at 10 — a pure view toggle with zero requests.
       */
      top10: Record<
        'most' | 'least' | 'up' | 'down',
        { personaId: string; name: string; value: number; delta: number | null }[]
      >;
    }
  >;
  /**
   * 90 daily points, newest last; the hero chart takes the last 14.
   * Uniform mean over personas that have a bucket that day.
   * A day with no launch is `{ n: 0, presence: null }`, KEPT: the chart draws a gap.
   */
  series: { d: string; presence: number | null; n: number }[];
  computedAt: string;
}

/**
 * PK PERSONA#<personaId> · SK PXDAY#<YYYY-MM-DD>#<launchId>, TTL CONVERSATION_PXDAY_TTL_DAYS.
 * Carries exactly the `ConversationPersonaRollup.lastLaunch` payload, so ~8–20 KB per launch.
 *
 * Why the row exists: the persona page's three identity blocks (« Face aux concurrents »,
 * « Cibles citées », « Sources les plus citées ») were hard-wired to `lastLaunch`, i.e. ONE
 * launch, because `ConversationDayBucket` keeps counters only and carries no domain, brand or
 * target identity at any window. A request-time fan-out over `RUNX#` is not an option either:
 * the SK is `RUNX#<cellKey>#<launchId>`, so there is no per-launch prefix to range-query; 90 d
 * can mean ~1080 rows at 20–50 KB each; and `pruneIds` collapses rows past
 * RETENTION_FLOOR_DAYS to about one per quarter, so such a read would silently return a
 * SAMPLED period while looking complete. This row is that fan-out, precomputed one launch at
 * a time from an object the rollup already holds.
 *
 * The `#<launchId>` suffix is load-bearing: several launches can close on the same UTC day, so
 * keying by day alone would force a read-modify-write that loses updates when two closes race.
 * With it every write is a pure Put (self-healing, idempotent on retry) and the reader simply
 * sums every row in the range.
 *
 * Summing day-level top-20s is a top-N-of-top-N, so any window total folded from these rows is
 * a LOWER BOUND, never a total: a domain that ranked 21st every day contributes 0. The
 * residuals are what makes that disclosable — never drop them.
 *
 * MUST NOT carry `dueBucket` — the table's only GSI is sparse on that attribute and the sweep
 * would pick the row up as a due persona.
 */
export interface ConversationPersonaDayExtras {
  tenantId: string;
  brandId: string;
  personaId: string;
  /** UTC day, `YYYY-MM-DD`. Derived from launchTs(launchId) — the CLAIMED tick (D46). */
  day: string;
  launchId: string;
  /** Capped at 20 after the cross-cell fold, same as `lastLaunch`. */
  topDomains: ConversationPersonaRollup['lastLaunch']['topDomains'];
  /** Capped at 30 after the fold. */
  topTargets: ConversationPersonaRollup['lastLaunch']['topTargets'];
  stageTurns: Record<ConversationStage, number>;
  /** Capped at 20 after the fold. */
  mentionCounts: Record<string, number>;
  residualDomains: { count: number; cit: number };
  residualTargets: { count: number; cit: number };
  residualBrands: { count: number; turns: number };
  computedAt: string;
}

/**
 * GAP-2 — PK BRAND#<brandId> · SK TDAY#<YYYY-MM-DD>, TTL CONVERSATION_TDAY_TTL_DAYS.
 * One row per brand per UTC day; the only row block ⑧ reads.
 *
 * Written as a FULL Put from the `TSET#` readback, never incremented — which is also what
 * makes it self-heal, because persona delete reaps a hardcoded 3-SK list and never touches it.
 *
 * The two arrays answer the two tabs and are deliberately NOT derivable from each other:
 * a product cited 10 times in a day contributes 10 to `citByCategory` and 1 to
 * `distinctByCategory`. Both are indexed by TARGET_CATEGORIES.
 *
 * MUST NOT carry `dueBucket` — the table's only GSI is sparse on that attribute and the
 * sweep would pick the row up as a due persona.
 */
export interface ConversationDayTargets {
  tenantId: string;
  brandId: string;
  /** UTC day, `YYYY-MM-DD`. Derived from launchTs(launchId) — the CLAIMED tick (D46). */
  day: string;
  /** Σ `cit` per category. Computed pre-cap, so never a floor. */
  citByCategory: number[];
  /** |{ targetId }| per category, exact within the day unless `truncated`. */
  distinctByCategory: number[];
  /**
   * A cap tripped (the per-sub-run id cap, or the 400 KB item ceiling on `TSET#`).
   * BOTH numbers are then floors and the block foot must say so.
   */
  truncated?: boolean;
  computedAt: string;
}

/**
 * GAP-2 — PK BRAND#<brandId> · SK TSET#<YYYY-MM-DD>, TTL CONVERSATION_TSET_TTL_DAYS.
 * The day's working id set. NEVER read by the API: it is accumulated and read back inside
 * one UpdateItem, then discarded. Modelled here only so the attribute names have one home.
 *
 * Seven top-level attributes per measure rather than a `number[7]` + a list of sets, because
 * `ADD` is the only operator that unions a set and `ADD` into an absent nested path throws.
 * Keyed by category NAME so the frozen TARGET_CATEGORIES order stays out of the wire format.
 *
 * `ADD` cannot retract: deleting a persona does not pull its ids back out. That is intended —
 * the row expires in days and a past day's count is history, not a current-state projection.
 */
export interface ConversationDayTargetSet {
  /** `s_<category>` → cited targetIds, unioned server-side. */
  /** `c_<category>` → Σ cit, summed server-side. */
  /** `folded` → launchIds already folded in; the idempotency guard for the numeric ADDs. */
  folded: string[];
}

/** GAP-2 — attribute name for the per-category id set on a `TSET#` row. */
export const conversationDaySetAttr = (category: TargetCategory) => `s_${category}`;
/** GAP-2 — attribute name for the per-category citation sum on a `TSET#` row. */
export const conversationDayCitAttr = (category: TargetCategory) => `c_${category}`;

/**
 * Lot I2 (C15, 00b DQ11) — `GET /brands/:b/conversations/load`, and the `load` object every
 * conversation save response carries (recomputed AFTER the write).
 */
export interface ConversationLoad {
  /** The ONE cadence every active persona resolves to; `null` = legacy per-persona cadences differ. */
  cadence: Cadence | null;
  /** `auditConfig.conversationCadence` is present. */
  cadenceIsSet: boolean;
  /** `conversationFigures(tenant, brandId)` — the same object `GET .../allowances` serves. */
  figures: import('./brand-allowances').AllowanceFigures;
  /** The brand's next conversation launch (`brandNextLaunch`); absent = nothing scheduled. */
  nextLaunchAt?: string;
  /** Agency: Σ runs per launch × turns × 2 × launches per period, per persona; else `null`. */
  brandCreditsPerMonth: number | null;
}
